import * as React from "react";
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";
import styled, { css } from "styled-components";
import upperCase from "lodash/upperCase";
import { Modal, Button } from "components/bootstrap";
import usePluginEntities from "hooks/usePluginEntities";
import generateId from "logic/generateId";
import useView from "views/hooks/useView";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { addWidget } from "views/logic/slices/widgetActions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
const WidgetList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.7rem;
`;
const CreateWidgetButton = styled(Button)(
  ({ theme }) => css`
    background-color: transparent;
    border-color: ${theme.colors.variant.gray};
    border-radius: 4px;
    height: 8rem;
    width: 8rem;
  `
);
const ButtonInner = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  overflow-wrap: break-word;
  white-space: break-spaces;
  text-align: center;
  gap: 0.3rem;
`;
const HugeIcon = styled.div(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.huge};
  `
);
const CreateNewWidgetModal = ({ onCancel, position }) => {
  const { t } = useTranslation("search");
  const creators = usePluginEntities("widgetCreators");
  const view = useView();
  const dispatch = useViewsDispatch();
  const sendTelemetry = useSendTelemetry("search-widget");
  const { setWidgetEditing } = useContext(WidgetFocusContext);
  const widgetButtons = useMemo(
    () => creators.map(({ title, titleKey, func, icon: WidgetIcon }) => {
      const displayedTitle = titleKey ? t(titleKey) : title;
      const onClick = async () => {
        var _a;
        sendTelemetry(
          (_a = TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_CREATE[upperCase(title).replace(/ /g, "_")]) != null ? _a : `Search Widget ${title} Created`,
          {}
        );
        const newId = generateId();
        const newWidget = func({ view }).toBuilder().id(newId).build();
        const result = await dispatch(addWidget(newWidget, position));
        await setWidgetEditing(newId);
        return result;
      };
      return /* @__PURE__ */ React.createElement(
        CreateWidgetButton,
        {
          key: title,
          type: "button",
          title: t("widget_creation.create_widget", { title: displayedTitle }),
          onClick
        },
        /* @__PURE__ */ React.createElement(ButtonInner, null, /* @__PURE__ */ React.createElement(HugeIcon, null, /* @__PURE__ */ React.createElement(WidgetIcon, null)), displayedTitle)
      );
    }),
    [creators, dispatch, position, sendTelemetry, setWidgetEditing, t, view]
  );
  return /* @__PURE__ */ React.createElement(Modal, { onHide: onCancel, show: true }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("widget_creation.title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(WidgetList, null, widgetButtons)), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: onCancel }, t("common:actions.cancel"))));
};
export default CreateNewWidgetModal;
